import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SETTINGS_PAGE_BG } from "@/common/components/settings/settingsList";
import cn from "@/common/utils/classNames";

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Telegram 13's tabs on a flat grey track 35px tall: nothing scrolls under them here, so a shadow
 * would only dirty the edge. A 28px white pill slides to the chosen tab in 200ms and takes its
 * width. Like Telegram's profile tabs the capsule
 * hugs a few tabs and stays centered: stretched, two tabs read as a form control with a huge
 * pill. Too many tabs fill the width and scroll inside.
 * items: [{ id, label, badge? }]
 */
export default function PillTabs({ items, value, onChange, label, className = "" }) {
  const listRef = useRef(null);
  const [pill, setPill] = useState(null);

  useIsoLayoutEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const tab = list.querySelector('[aria-selected="true"]');
      setPill(tab ? { left: tab.offsetLeft, width: tab.offsetWidth } : null);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    list.querySelectorAll('[role="tab"]').forEach((tab) => observer.observe(tab));
    return () => observer.disconnect();
  }, [value, items]);

  return (
    <div className={cn("relative mx-auto flex h-[35px] w-fit max-w-full shrink-0 rounded-full", SETTINGS_PAGE_BG, className)}>
      {/* taller than the 35px track it sits on: each tab is a 40px target, the look stays */}
      <div
        ref={listRef}
        role="tablist"
        aria-label={label}
        className="relative -my-[2.5px] flex h-10 min-w-0 overflow-x-auto px-[3.5px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {pill && (
          <span
            aria-hidden
            className="absolute top-1.5 h-7 rounded-full bg-white transition-[left,width] duration-200 ease-out"
            style={{ left: pill.left, width: pill.width }}
          />
        )}
        {items.map((item) => {
          const on = item.id === value;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => onChange?.(item.id)}
              className={cn(
                "relative isolate flex h-10 shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-0 bg-transparent px-3.5 text-[14.5px] font-medium transition-colors duration-200 [font-family:inherit]",
                on ? "text-primary" : "text-[#6f747b]",
                on && !pill && "before:absolute before:inset-x-0 before:top-1.5 before:-z-10 before:h-7 before:rounded-full before:bg-white"
              )}
            >
              {item.label}
              {item.badge ? (
                <span
                  className="min-w-[18px] rounded-full bg-primary px-1 text-center text-[12px] font-semibold leading-[18px] text-white"
                >
                  {item.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
